import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { CalendarDays, Plus, Minus } from "lucide-react";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { DateField } from "@/components/calculator/DateField";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

import { useAppStore } from "@/lib/store";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

import {
  yearsBetween,
  birthYearFromAge,
  banglaYearsForGregorian,
  approxHijriYear,
} from "@/lib/calculators/yearCalculator";
import { gregorianToBangla } from "@/lib/bengaliDate";

type B = { en: string; bn: string };
type Lang = "en" | "bn";
type TabId = "between" | "birth" | "addsub";

const T = {
  h1: { en: "Year Calculator", bn: "ইয়ার ক্যালকুলেটর" },
  desc: {
    en: "Calculate years between dates, find your birth year from age, or add/subtract years — with Bengali (Bangla shon) and Hijri year context.",
    bn: "দুই তারিখের মধ্যে বছর, বয়স থেকে জন্ম সাল, অথবা বছর যোগ/বিয়োগ — বাংলা সন ও হিজরি বছর সহ।",
  },
  tBetween: { en: "Years between", bn: "বছরের ব্যবধান" },
  tBirth: { en: "Birth year from age", bn: "বয়স → জন্ম সাল" },
  tAddSub: { en: "Add / Subtract years", bn: "বছর যোগ/বিয়োগ" },

  start: { en: "Start date", bn: "শুরুর তারিখ" },
  end: { en: "End date", bn: "শেষের তারিখ" },
  asOf: { en: "As of date", bn: "যে তারিখে" },
  age: { en: "Current age (years)", bn: "বর্তমান বয়স (বছর)" },
  startYear: { en: "Starting year", bn: "শুরুর বছর" },
  yearsCount: { en: "Years", bn: "বছর" },
  add: { en: "Add", bn: "যোগ" },
  sub: { en: "Subtract", bn: "বিয়োগ" },

  bnDigits: { en: "Bengali numerals", bn: "বাংলা সংখ্যা" },
  presets: { en: "Presets", bn: "প্রিসেট" },
  banglaNow: { en: "Current Bangla year", bn: "বর্তমান বাংলা সন" },
  hijriNow: { en: "Current Hijri year", bn: "বর্তমান হিজরি সন" },

  // outputs
  full: { en: "Full years", bn: "পূর্ণ বছর" },
  decimal: { en: "Decimal years", bn: "দশমিক বছর" },
  totalMonths: { en: "Total months", bn: "মোট মাস" },
  totalDays: { en: "Total days", bn: "মোট দিন" },
  bengali: { en: "Bengali year", bn: "বাংলা সন" },
  hijri: { en: "Hijri year", bn: "হিজরি সন" },
} as const;

const FAQ: { q: B; a: B }[] = [
  {
    q: { en: "What's the current Bangla year?", bn: "বর্তমান বাংলা সন কত?" },
    a: {
      en: "Bangla year is calculated as Gregorian year − 593 (after April 14) or − 594 (before). For example, May 2026 corresponds to Bangla 1433 (১৪৩৩), and January 2026 was still 1432.",
      bn: "বাংলা সন = গ্রেগরীয় − ৫৯৩ (১৪ এপ্রিলের পর) অথবা − ৫৯৪ (আগে)। যেমন মে ২০২৬ = বাংলা ১৪৩৩, জানুয়ারি ২০২৬ ছিল ১৪৩২।",
    },
  },
  {
    q: { en: "How to find birth year from age?", bn: "বয়স থেকে জন্ম সাল কীভাবে?" },
    a: {
      en: "If you are N years old as of today, your birth year is either today's year − N (if your birthday has already passed) or − (N+1) (if not). Because age covers a full year, the result is given as a range of birth dates.",
      bn: "আজ N বছর বয়স হলে, জন্ম সাল = চলতি বছর − N (জন্মদিন পেরিয়ে গেলে) অথবা − (N+1) (না পেরোলে)। বয়স পুরো একটি বছর জুড়ে থাকে, তাই ফলাফল একটি রেঞ্জ আকারে দেখানো হয়।",
    },
  },
  {
    q: { en: "বাংলা সন এখন কত?", bn: "বাংলা সন এখন কত?" },
    a: {
      en: "উপরের যে কোনো ট্যাবে আজকের তারিখ বসিয়ে দেখুন — বর্তমান বাংলা সন স্বয়ংক্রিয়ভাবে গণনা হবে। ১৪ এপ্রিলের পর নতুন বাংলা বছর শুরু হয়।",
      bn: "উপরের যে কোনো ট্যাবে আজকের তারিখ বসিয়ে দেখুন — বর্তমান বাংলা সন স্বয়ংক্রিয়ভাবে গণনা হবে। ১৪ এপ্রিলের পর নতুন বাংলা বছর শুরু হয়।",
    },
  },
  {
    q: { en: "হিজরি সন কীভাবে গণনা হয়?", bn: "হিজরি সন কীভাবে গণনা হয়?" },
    a: {
      en: "হিজরি বছর চান্দ্র — প্রতি বছর গ্রেগরীয়র চেয়ে ১০–১১ দিন ছোট। আনুমানিক সূত্র: হিজরি ≈ (গ্রেগরীয় − ৬২২) × ৩৩/৩২। আমরা ট্যাবুলার (কুয়েতি) অ্যালগরিদম ব্যবহার করি — চাঁদ দেখার তারিখ থেকে ±১ দিন ভিন্ন হতে পারে।",
      bn: "হিজরি বছর চান্দ্র — প্রতি বছর গ্রেগরীয়র চেয়ে ১০–১১ দিন ছোট। আনুমানিক সূত্র: হিজরি ≈ (গ্রেগরীয় − ৬২২) × ৩৩/৩২। আমরা ট্যাবুলার (কুয়েতি) অ্যালগরিদম ব্যবহার করি — চাঁদ দেখার তারিখ থেকে ±১ দিন ভিন্ন হতে পারে।",
    },
  },
  {
    q: { en: "Why does a Gregorian year map to two Bangla years?", bn: "একটি গ্রেগরীয় বছর কেন দুটি বাংলা সনে পড়ে?" },
    a: {
      en: "Because Bangla year starts on Pohela Boishakh (14 April), the first part of any Gregorian year (Jan–13 Apr) belongs to one Bangla year, and the rest (14 Apr–Dec) to the next. So 2026 spans Bangla 1432 and 1433.",
      bn: "বাংলা বছর পহেলা বৈশাখ (১৪ এপ্রিল) থেকে শুরু হয় — তাই গ্রেগরীয় বছরের শুরুর অংশ (জানু–১৩ এপ্রিল) এক বাংলা বছরে, বাকিটা (১৪ এপ্রিল–ডিসে) পরের বাংলা বছরে। ২০২৬ এ বাংলা ১৪৩২ ও ১৪৩৩।",
    },
  },
  {
    q: { en: "How accurate is the Hijri year shown?", bn: "হিজরি সনের নির্ভুলতা কতটুকু?" },
    a: {
      en: "We sample mid-year (1 July) and convert via the tabular Kuwaiti algorithm. For most months the result matches Bangladesh's official Hijri year, but during transitions it may differ by 1 due to moon-sighting rules.",
      bn: "আমরা ১ জুলাই-এর তারিখ ট্যাবুলার কুয়েতি অ্যালগরিদমে রূপান্তর করি। অধিকাংশ সময় বাংলাদেশের সরকারি হিজরি বছরের সাথে মিলে যায়, তবে পরিবর্তনের সময় ১ ভিন্ন হতে পারে।",
    },
  },
];

export const Route = createFileRoute("/year-calculator")({
  head: () => {
    const seo = buildSeo({
      title: "Year Calculator — Years Between Dates",
      description:
        "Free year calculator. Calculate years between dates, find birth year from age, add/subtract years. Bengali calendar (Bangla shon) and Hijri year support. ইয়ার ক্যালকুলেটর।",
      path: "/year-calculator",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Year Calculator", path: "/year-calculator" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: YearCalculatorPage,
});

const HASH_TO_TAB: Record<string, TabId> = { between: "between", birth: "birth", addsub: "addsub" };

function YearCalculatorPage() {
  const lang = useAppStore((s) => s.lang) as Lang;
  const tr = (k: keyof typeof T) => (T[k] as B)[lang];
  const [bnDigits, setBnDigits] = useState(false);

  const [tab, setTab] = useState<TabId>("between");
  useEffect(() => {
    if (typeof window === "undefined") return;
    const h = window.location.hash.replace("#", "");
    if (HASH_TO_TAB[h]) setTab(HASH_TO_TAB[h]);
  }, []);
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash.replace("#", "") !== tab) {
      window.history.replaceState(null, "", `#${tab}`);
    }
  }, [tab]);

  const today = useMemo(() => new Date(), []);
  const [start, setStart] = useState<Date | null>(new Date(1992, 0, 1));
  const [end, setEnd] = useState<Date | null>(today);
  const [ageStr, setAgeStr] = useState("30");
  const [asOf, setAsOf] = useState<Date | null>(today);
  const [yearStr, setYearStr] = useState("1990");
  const [yearsStr, setYearsStr] = useState("35");
  const [op, setOp] = useState<"add" | "sub">("add");

  const useBn = lang === "bn" || bnDigits;
  const num = (n: number | string) => (useBn ? toBengaliNumerals(n) : String(n));
  const fmtN = (n: number) => num(n.toLocaleString("en-US"));
  const fmtDate = (d: Date) => {
    const s = format(d, "d MMM yyyy");
    return useBn ? toBengaliNumerals(s) : s;
  };

  // Tab 1
  const between = useMemo(() => (start && end ? yearsBetween(start, end) : null), [start, end]);

  // Tab 2
  const birth = useMemo(() => {
    const a = parseInt(ageStr, 10);
    if (!asOf || !Number.isFinite(a) || a < 0) return null;
    return birthYearFromAge(a, asOf);
  }, [ageStr, asOf]);

  // Tab 3
  const result = useMemo(() => {
    const y = parseInt(yearStr, 10);
    const k = parseInt(yearsStr, 10);
    if (!Number.isFinite(y) || !Number.isFinite(k)) return null;
    const out = op === "add" ? y + k : y - k;
    const bangla = banglaYearsForGregorian(out);
    const hijri = approxHijriYear(out);
    return { year: out, bangla, hijri };
  }, [yearStr, yearsStr, op]);

  const currentBangla = useMemo(() => gregorianToBangla(today).year, [today]);
  const currentHijri = useMemo(() => approxHijriYear(today.getFullYear()), [today]);

  return (
    <CalculatorLayout
      slug="year-calculator"
      title={tr("h1")}
      description={tr("desc")}
      iconName="CalendarDays"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className={cn("flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
          <span>{tr("presets")}:</span>
          <span className="rounded-full border border-border bg-card px-2.5 py-1">
            {tr("banglaNow")}: <strong className="text-foreground">{num(currentBangla)}</strong>
          </span>
          <span className="rounded-full border border-border bg-card px-2.5 py-1">
            {tr("hijriNow")}: <strong className="text-foreground">{num(currentHijri)}</strong>
          </span>
        </div>
        <label className={cn("inline-flex items-center gap-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
          <input type="checkbox" checked={bnDigits} onChange={(e) => setBnDigits(e.target.checked)} className="h-3.5 w-3.5 accent-accent" />
          {tr("bnDigits")}
        </label>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as TabId)}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="between" className={cn(lang === "bn" && "font-bn")}>{tr("tBetween")}</TabsTrigger>
          <TabsTrigger value="birth" className={cn(lang === "bn" && "font-bn")}>{tr("tBirth")}</TabsTrigger>
          <TabsTrigger value="addsub" className={cn(lang === "bn" && "font-bn")}>{tr("tAddSub")}</TabsTrigger>
        </TabsList>

        {/* Tab 1 */}
        <TabsContent value="between" className="mt-6 space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <DateField label={tr("start")} value={start} onChange={setStart} fromYear={1900} toDate={new Date(2100, 11, 31)} />
            <DateField label={tr("end")} value={end} onChange={setEnd} fromYear={1900} toDate={new Date(2100, 11, 31)} />
          </div>
          {between && start && end && (
            <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
              <div className="rounded-2xl border border-accent/40 bg-accent/5 p-5 text-center">
                <p className="text-3xl font-bold tabular-nums md:text-4xl">
                  {fmtN(between.years)} <span className="text-lg text-muted-foreground">{tr("yearsCount")}</span>
                  {between.months > 0 && (
                    <>, {fmtN(between.months)} <span className="text-lg text-muted-foreground">{lang === "en" ? "months" : "মাস"}</span></>
                  )}
                </p>
                <p className={cn("mt-1 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
                  {fmtDate(start)} → {fmtDate(end)}
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-4">
                <Stat label={tr("decimal")} value={num(between.totalYearsDecimal.toFixed(2))} />
                <Stat label={tr("totalMonths")} value={fmtN(between.totalMonths)} />
                <Stat label={tr("totalDays")} value={fmtN(between.totalDays)} />
                <Stat label={`${tr("yearsCount")}/M/D`} value={`${num(between.years)} / ${num(between.months)} / ${num(between.days)}`} />
              </div>
              <CalendarRow
                from={start}
                to={end}
                lang={lang}
                useBn={useBn}
              />
            </motion.div>
          )}
        </TabsContent>

        {/* Tab 2 */}
        <TabsContent value="birth" className="mt-6 space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <NumField label={tr("age")} value={ageStr} onChange={setAgeStr} lang={lang} useBn={useBn} />
            <DateField label={tr("asOf")} value={asOf} onChange={setAsOf} fromYear={1900} toDate={new Date(2100, 11, 31)} />
          </div>
          {birth && asOf && (
            <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
              <div className="rounded-2xl border border-accent/40 bg-accent/5 p-5 text-center">
                <p className={cn("text-xs uppercase tracking-wide text-muted-foreground", lang === "bn" && "font-bn")}>
                  {lang === "en" ? "Born between" : "জন্মের সম্ভাব্য তারিখ"}
                </p>
                <p className="mt-1 text-2xl font-bold tabular-nums md:text-3xl">
                  {fmtDate(birth.earliest)} — {fmtDate(birth.latest)}
                </p>
                <p className={cn("mt-2 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
                  {lang === "en"
                    ? `If you are ${num(ageStr)} years old as of ${fmtDate(asOf)}, you were born in ${num(birth.earliestYear)} or ${num(birth.latestYear)}.`
                    : `${fmtDate(asOf)} তারিখে ${num(ageStr)} বছর বয়স হলে, আপনি ${num(birth.earliestYear)} বা ${num(birth.latestYear)} সালে জন্মেছেন।`}
                </p>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <Stat
                  label={tr("bengali")}
                  value={`${num(gregorianToBangla(birth.earliest).year)} – ${num(gregorianToBangla(birth.latest).year)}`}
                />
                <Stat
                  label={tr("hijri")}
                  value={`${num(approxHijriYear(birth.earliestYear))} – ${num(approxHijriYear(birth.latestYear))}`}
                />
              </div>
            </motion.div>
          )}
        </TabsContent>

        {/* Tab 3 */}
        <TabsContent value="addsub" className="mt-6 space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <NumField label={tr("startYear")} value={yearStr} onChange={setYearStr} lang={lang} useBn={useBn} />
            <div className="space-y-1.5">
              <label className={cn("block text-sm font-medium", lang === "bn" && "font-bn")}>{lang === "en" ? "Operation" : "অপারেশন"}</label>
              <div className="flex gap-2">
                {(["add", "sub"] as const).map((o) => (
                  <button
                    key={o}
                    type="button"
                    onClick={() => setOp(o)}
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-full border px-3 py-2 text-xs font-medium",
                      op === o ? "border-accent bg-accent text-accent-foreground" : "border-border bg-card hover:border-accent/40",
                      lang === "bn" && "font-bn",
                    )}
                  >
                    {o === "add" ? <Plus size={12} /> : <Minus size={12} />}
                    {o === "add" ? tr("add") : tr("sub")}
                  </button>
                ))}
              </div>
            </div>
            <NumField label={tr("yearsCount")} value={yearsStr} onChange={setYearsStr} lang={lang} useBn={useBn} />
          </div>
          {result && (
            <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
              <div className="rounded-2xl border border-accent/40 bg-accent/5 p-5 text-center">
                <p className={cn("text-xs uppercase tracking-wide text-muted-foreground", lang === "bn" && "font-bn")}>
                  {num(yearStr)} {op === "add" ? "+" : "−"} {num(yearsStr)} {tr("yearsCount")} =
                </p>
                <p className="mt-1 text-4xl font-bold tabular-nums md:text-5xl">{num(result.year)}</p>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <Stat label={tr("bengali")} value={`${num(result.bangla.early)} – ${num(result.bangla.late)}`} />
                <Stat label={tr("hijri")} value={num(result.hijri) + " " + (lang === "en" ? "(approx.)" : "(আনুমানিক)")} />
              </div>
            </motion.div>
          )}
        </TabsContent>
      </Tabs>

      {/* Educational content */}
      <article className="mt-12 space-y-10">
        <Section title={lang === "en" ? "How year calculation works" : "বছর গণনা কীভাবে হয়"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "We count full years from start to end first — i.e., the date in the end year that's on or before the start's anniversary. The remainder becomes months and days. Decimal years use 365.25 days/year to account for leap years."
              : "প্রথমে পূর্ণ বছর গণনা — অর্থাৎ শুরু তারিখের বার্ষিক পুনরাবৃত্তি যত পেরিয়েছে। অবশিষ্ট মাস ও দিন। দশমিক বছর গণনায় লিপ ইয়ারের জন্য ৩৬৫.২৫ দিন/বছর ধরা হয়।"}
          </p>
        </Section>

        <Section title={lang === "en" ? "Bengali year vs Gregorian" : "বাংলা সন বনাম গ্রেগরীয়"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "Bangla year starts on 14 April (Pohela Boishakh). So a Gregorian year always overlaps two Bangla years — for example, 2026 is partly 1432 (Jan–13 Apr) and partly 1433 (14 Apr onwards). The current Bangla year is shown in the preset chip above."
              : "বাংলা বছর ১৪ এপ্রিল (পহেলা বৈশাখ) থেকে শুরু — তাই একটি গ্রেগরীয় বছর সবসময় দুটি বাংলা বছরে পড়ে। উদাহরণ: ২০২৬ এর জানু–১৩ এপ্রিল = ১৪৩২, ১৪ এপ্রিল-এর পর = ১৪৩৩। বর্তমান বাংলা সন উপরে দেখুন।"}
          </p>
        </Section>

        <Section title={lang === "en" ? "Hijri year vs Gregorian" : "হিজরি বনাম গ্রেগরীয়"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "The Hijri year is lunar — about 354–355 days. So Hijri 'gains' 11 days a year on the Gregorian calendar, completing one extra cycle every ~33 years. Approximate formula: Hijri ≈ (Gregorian − 622) × 33/32."
              : "হিজরি বছর চান্দ্র — ৩৫৪–৩৫৫ দিন। প্রতি বছর গ্রেগরীয়র চেয়ে ১১ দিন এগিয়ে যায়, ~৩৩ বছরে একটি অতিরিক্ত চক্র সম্পন্ন। আনুমানিক সূত্র: হিজরি ≈ (গ্রেগরীয় − ৬২২) × ৩৩/৩২।"}
          </p>
        </Section>

        <Section title={lang === "en" ? "Why birth year is a range" : "জন্ম সাল কেন রেঞ্জ"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "Age N covers the full year from your N-th to (N+1)-th birthday. So 'I am 30 today' means you were born any time in the 365-day window ending today minus 30 years. We show both possible birth years."
              : "N বছর বয়স মানে আপনার N-তম থেকে (N+১)-তম জন্মদিন পর্যন্ত পুরো একটি বছর। তাই 'আজ আমি ৩০' মানে আজ থেকে ৩০ বছর পেছনের ৩৬৫ দিনের যেকোনো দিন। উভয় সম্ভাব্য বছর দেখানো হয়।"}
          </p>
        </Section>

        <Section title={lang === "en" ? "Related calculators" : "সম্পর্কিত ক্যালকুলেটর"} lang={lang}>
          <div className="flex flex-wrap gap-2 text-sm">
            <Link to="/age-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">{lang === "en" ? "Age" : "বয়স"}</Link>
            <Link to="/date-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">{lang === "en" ? "Date" : "তারিখ"}</Link>
          </div>
        </Section>
      </article>
    </CalculatorLayout>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-3">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-xl font-semibold tabular-nums">{value}</p>
    </div>
  );
}

function NumField({
  label, value, onChange, lang, useBn,
}: { label: string; value: string; onChange: (v: string) => void; lang: Lang; useBn: boolean }) {
  const display = useBn ? toBengaliNumerals(value) : value;
  return (
    <div className="space-y-1.5">
      <label className={cn("block text-sm font-medium", lang === "bn" && "font-bn")}>{label}</label>
      <input
        type="text"
        inputMode="numeric"
        value={display}
        onChange={(e) =>
          onChange(
            e.target.value
              .replace(/[০-৯]/g, (d) => String("০১২৩৪৫৬৭৮৯".indexOf(d)))
              .replace(/[^0-9-]/g, ""),
          )
        }
        className="w-full rounded-lg border border-border bg-muted px-3 py-2.5 text-base tabular-nums outline-none focus:border-accent focus:bg-background focus:ring-2 focus:ring-accent/20"
      />
    </div>
  );
}

function CalendarRow({ from, to, lang, useBn }: { from: Date; to: Date; lang: Lang; useBn: boolean }) {
  const fb = gregorianToBangla(from);
  const tb = gregorianToBangla(to);
  const num = (n: number | string) => (useBn ? toBengaliNumerals(n) : String(n));
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="mb-1 flex items-center gap-1.5 text-xs uppercase tracking-wide text-muted-foreground">
        <CalendarDays size={12} /> {lang === "en" ? "Bengali calendar context" : "বাংলা সন প্রসঙ্গ"}
      </div>
      <p className={cn("text-sm", lang === "bn" && "font-bn")}>
        {lang === "en" ? "From" : "শুরু"} <strong>{num(fb.year)}</strong> {lang === "en" ? "to" : "থেকে"} <strong>{num(tb.year)}</strong>
        {" · "}
        Hijri <strong>{num(approxHijriYear(from.getFullYear()))}</strong> → <strong>{num(approxHijriYear(to.getFullYear()))}</strong>
      </p>
    </div>
  );
}

function Section({ title, children, lang }: { title: string; children: React.ReactNode; lang: Lang }) {
  return (
    <section>
      <h2 className={cn("mb-3 text-lg font-bold tracking-tight", lang === "bn" && "font-bn")}>{title}</h2>
      {children}
    </section>
  );
}
